import React, { useState, useRef, useEffect } from 'react';
import { Box, Menu, MenuItem, Typography } from '@mui/material';
import { Language } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

const LanguageSwitcher = () => {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const { t, i18n } = useTranslation();
  
  // Create refs for the draggable element
  const dragRef = useRef(null);
  
  // State for storing position
  const [position, setPosition] = useState({ x: 24, y: 24 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  
  // Handle language menu
  const handleClick = (event) => {
    if (!isDragging) {
      setAnchorEl(event.currentTarget);
    }
  };
  
  const handleClose = () => {
    setAnchorEl(null);
  };
  
  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
    handleClose();
  };
  
  // Handle drag start
  const handleMouseDown = (e) => {
    if (dragRef.current) {
      const rect = dragRef.current.getBoundingClientRect();
      setDragOffset({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top
      });
      setIsDragging(true);
    }
  };
  
  // Handle drag movement
  const handleMouseMove = (e) => {
    if (isDragging) {
      e.preventDefault();
      
      // Calculate new position
      const x = e.clientX - dragOffset.x;
      const y = e.clientY - dragOffset.y;
      
      // Keep within viewport bounds
      const maxX = window.innerWidth - 60;
      const maxY = window.innerHeight - 60;
      
      const boundedX = Math.min(Math.max(0, x), maxX);
      const boundedY = Math.min(Math.max(0, y), maxY);
      
      setPosition({
        x: boundedX,
        y: boundedY
      });
    }
  };
  
  // Handle drag end
  const handleMouseUp = () => {
    setIsDragging(false);
  };
  
  // Add and remove event listeners
  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      return () => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging]);
  
  // Determine initial position (bottom right corner)
  useEffect(() => {
    setPosition({
      x: window.innerWidth - 84,
      y: window.innerHeight - 84
    });
  }, []);

  return (
    <div 
      ref={dragRef}
      style={{
        position: 'fixed',
        left: `${position.x}px`,
        top: `${position.y}px`,
        zIndex: 9999,
        cursor: isDragging ? 'grabbing' : 'grab'
      }}
    >
      <div 
        style={{
          width: '60px',
          height: '60px',
          borderRadius: '50%',
          backgroundColor: '#0A6621',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '0 4px 15px rgba(0,0,0,0.3)',
          border: '2px solid white',
          transition: 'all 0.2s ease',
          transform: isDragging ? 'scale(1.1)' : 'scale(1)'
        }}
        onClick={handleClick}
        onMouseDown={handleMouseDown}
        aria-controls={open ? 'language-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
      >
        <Language style={{ fontSize: '28px' }} />
      </div>
      
      <Menu
        id="language-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          'aria-labelledby': 'language-button',
        }}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        sx={{ zIndex: 10000 }}
      >
        <MenuItem onClick={() => changeLanguage('en')} selected={i18n.language === 'en'}>
          <Box sx={{ display: 'flex', alignItems: 'center', px: 2, py: 1 }}>
            <Typography variant="body1" sx={{ fontWeight: i18n.language === 'en' ? 'bold' : 'normal' }}>
              English
            </Typography>
          </Box>
        </MenuItem>
        <MenuItem onClick={() => changeLanguage('ja')} selected={i18n.language === 'ja'}>
          <Box sx={{ display: 'flex', alignItems: 'center', px: 2, py: 1 }}>
            <Typography variant="body1" sx={{ fontWeight: i18n.language === 'ja' ? 'bold' : 'normal' }}>
              日本語
            </Typography>
          </Box>
        </MenuItem>
      </Menu>
    </div>
  );
};

export default LanguageSwitcher;